import * as _ from "lodash";
import * as Redux from "redux";
import { IProcessData } from "../model/IProcessData";
import { State } from "../model/State";

import * as AppPath from "../model/AppPath";

import { addElementIdsToValidation, rebuildVariableScopesAndTriggerValidationAction } from "../actions/ValidateProcess";

import { ProblemDetails, ThunkResult, Utilities } from "../../shared/utilities";
import { IAction } from "../model/IAction";
import { discardDraft } from "./DiscardDraftAction";
import { defaultAgentGroupName } from '../../shared/model/AgentGroup';
import { requestAndRedirect } from '../shared/model/SaveChangesAndRun';
import { removeFromLocalStorage } from "./ProcessToLocalStorageActions";

// tslint:disable:no-console

export function saveProcessDraft(changeDescription: string): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        dispatch(savingProcess());
        try {
            const state: State = getState();
            // Draft can be saved even though the process is invalid, so don't to validation here.
            const processData: IProcessData = Object.assign({} as IProcessData, state.process);
            const prevVersionId = processData.id;
            processData.triggerData = [];
            processData.elementParameters = _.map(state.elements, (e) => e);
            processData.changeDescription = changeDescription;
            processData.processVariables = state.processVariables;

            const response = await Utilities.fetchBase(`api/process/processDraftBpmn`, processData, "POST");
            const id = await Utilities.parseJsonResponse(response, checkAndReportBuildError) as number;
            Utilities.toastSuccess(FRENDS.Localizations.Process_Saved);
            dispatch(saveProcessOk(prevVersionId));
            // If the original was an actual process, redirect to the new draft
            // - do not redirect if just saving on top of new draft
            if (!processData.isDraft) {
                const newProcessUrl = `${Utilities.getUrlBase() + (processData.isSubprocess ? "SubProcess" : "Process")}/Draft/${id}`;
                setTimeout(() => window.location.href = newProcessUrl, 1000);
            }
        } catch (err) {
            reportUnhandledException(err, dispatch, "Process save failed");
        }
    };
}

export function buildProcess(): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        dispatch(savingProcess());
        try {
            let state: State = getState();
            const elementIds = Object.keys(state.elements);

            dispatch(addElementIdsToValidation(elementIds));
            dispatch(rebuildVariableScopesAndTriggerValidationAction());

            state = getState();
            if (Object.keys(state.validation.validationDictionary).length > 0) {
                dispatch(saveProcessFailed());
                let hasShownErrors = false;
                Object.values(state.validation.validationDictionary).forEach(errors => {
                    errors.forEach(error => {
                        const message = error.getMessageString();
                        if (message) {
                            Utilities.toastError(message);
                            hasShownErrors = true;
                        }
                    });
                });
                // Only show generic message if no specific errors were shown
                if (!hasShownErrors) {
                    Utilities.toastError(FRENDS.Localizations.Validation_InvalidProcess);
                }
            } else {
                const processData: IProcessData = Object.assign({} as IProcessData, state.process);
                processData.elementParameters = _.map(state.elements, (e) => e);
                processData.validationOnly = true;
                processData.processVariables = state.processVariables;

                const response = await Utilities.fetchBase(`api/process/buildBpmn`, processData, "POST");
                await Utilities.parseJsonResponse(response, checkAndReportBuildError);
                dispatch(buildProcessOk());
                Utilities.toastSuccess(FRENDS.Localizations.CompilationSuccesful);
            }
        } catch (err) { // Unhandeled exception from the javascript
            reportUnhandledException(err, dispatch, FRENDS.Localizations.CompilationFailed);
        }
    };
}

export function saveProcess(changeDescription: string, activateIfNew: boolean, runImmediately?: boolean): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        dispatch(savingProcess());
        try {
            const elementIds = Object.keys((getState() as State).elements);
            dispatch(addElementIdsToValidation(elementIds));

            dispatch(rebuildVariableScopesAndTriggerValidationAction());
            const state: State = getState();
            if (Object.keys(state.validation.validationDictionary).length > 0) {
                dispatch(saveProcessFailed());
                let hasShownErrors = false;
                Object.values(state.validation.validationDictionary).forEach(errors => {
                    errors.forEach(error => {
                        const message = error.getMessageString();
                        if (message) {
                            Utilities.toastError(message);
                            hasShownErrors = true;
                        }
                    });
                });
                // Only show generic message if no specific errors were shown
                if (!hasShownErrors) {
                    Utilities.toastError(FRENDS.Localizations.Validation_InvalidProcess);
                }
            } else {
                const processData: IProcessData = Object.assign({} as IProcessData, state.process);
                const prevVersionId = processData.id;
                processData.elementParameters = _.map(state.elements, (e) => e);
                processData.processVariables = state.processVariables;
                processData.changeDescription = changeDescription;
                processData.activateIfNew = activateIfNew;

                const response = await Utilities.fetchBase(`api/process/processBpmn`, processData, "POST")
                const processBpmnData = await Utilities.parseJsonResponse<IProcessBpmn>(response, checkAndReportBuildError);

                Utilities.toastSuccess(FRENDS.Localizations.Process_Saved);
                dispatch(saveProcessOk(prevVersionId));
                // NewFromTemplate -action sets the redirectAfterSaveTo to the state,
                // so if it is there, use it to redirect back to the API management page.
                // Otherwise, go to the process list
                const redirectUri = state.uiState.redirectAfterSaveTo || AppPath.getProcessListLink(processBpmnData.processId);

                // After a successful save discard the possible old draft
                if (processData.isDraft) {
                    dispatch(discardDraft(processData.id)).then((processId) => {
                        setTimeout(() => window.location.href = redirectUri, 1000);
                    });
                } else if (runImmediately) {
                    const defaultAgentGroup = getState().agentGroups.find(x => x.name == defaultAgentGroupName);
                    if (defaultAgentGroup?.isCrossPlatform && processData.targetFramework === "net471") {
                        Utilities.toastError(`Process must target NET Standard 2.0 to run on Cross-Platform Agent group "${defaultAgentGroup.displayName}"`);
                        return;
                    }
                    // Get refreshed url when saved and ran
                    const newUrl = `${Utilities.getUrlBase() + (processData.isSubprocess ? "SubProcess" : "Process")}/Edit/${processBpmnData.processId}`;
                    window.history.pushState("", window.document.title, newUrl);

                    const manualTriggers = getState().process.manualTrigger;
                    if (!_.isEmpty(manualTriggers)) {
                        dispatch(openRunOnceDialog(processBpmnData.processId, processBpmnData.uniqueIdentifier));
                    } else {
                        dispatch(saveAndRunProcess(processBpmnData.processId, processBpmnData.uniqueIdentifier));
                        dispatch(requestAndRedirect(defaultAgentGroup?.name, defaultAgentGroup?.name, processBpmnData.processId, {}));
                    }
                } else {
                    setTimeout(() => window.location.href = redirectUri, 1000);
                }
            }
        } catch (err) {
            reportUnhandledException(err, dispatch, "Process save failed");
        }
    };
}

export function reportUnhandledException(msg: string | IDependencyConstraintError, dispatch: Redux.Dispatch<IAction>, toasterBaseMessage: string) {
    console.error(msg); 
    const asDepdenencyError = msg as IDependencyConstraintError;
    if (asDepdenencyError?.showToast === false) { 
        // No need to show toast instead show small modal
        dispatch(openDependencyConstrainSaveModal(asDepdenencyError.error));
        return;
    }
    Utilities.toastError(toasterBaseMessage + "\n\n" + msg);
    dispatch(saveProcessFailed());
}

export function checkAndReportBuildError(msg: ProblemDetails) : IDependencyConstraintError | string | undefined {
    if (msg.compilationErrors) {
        // compilation errors
        const errorMessages = msg.compilationErrors.map(err => `${err}`);
        const error = errorMessages.join('\n\n');

        // also report to console
        console.error(msg.compilationErrors);
        console.log(msg.sourceCode);

        return error;
    }

    if(msg.dependencyConstraints) {
        const errorMessages = msg.dependencyConstraints.map(err => `${err}`);
        const error = errorMessages.join('\n\n');

        return { showToast: false, error: error };
    }
}
export interface IDependencyConstraintError {
    error: string;
    showToast: boolean;
 }

interface IProcessBpmn {
    processId: number;
    uniqueIdentifier: string;
}

export interface OpenRunOnceAction extends IAction {
    processId: number;
    uniqueIdentifier: string;
}

export interface SaveAndRunProcessAction extends IAction {
    processId: number;
    uniqueIdentifier: string;
}

export const OPEN_RUN_ONCE_DIALOG = "OPEN_RUN_ONCE_DIALOG";
export function openRunOnceDialog(processId: number, uniqueIdentifier: string): OpenRunOnceAction {
    return {
        type: OPEN_RUN_ONCE_DIALOG,
        processId: processId,
        uniqueIdentifier: uniqueIdentifier
    };
}

export const SAVE_AND_RUN_PROCESS = "SAVE_AND_RUN_PROCESS";
export function saveAndRunProcess(processId: number, uniqueIdentifier: string): SaveAndRunProcessAction {
    return {
        type: SAVE_AND_RUN_PROCESS,
        processId: processId,
        uniqueIdentifier: uniqueIdentifier
    }
}

export const CLOSE_RUN_ONCE_DIALOG = "CLOSE_RUN_ONCE_DIALOG";
export function closeRunOnceDialog(): Redux.Action {
    return {
        type: CLOSE_RUN_ONCE_DIALOG
    };
}

export const SAVE_PROCESS = "SAVE_PROCESS";
export function savingProcess() {
    return {
        type: SAVE_PROCESS
    };
}

export const SAVE_PROCESS_OK = "SAVE_PROCESS_OK";
export function saveProcessOk(processId: number) {
    removeFromLocalStorage(processId);
    return {
        type: SAVE_PROCESS_OK
    };
}

export const BUILD_PROCESS_OK = "BUILD_PROCESS_OK";
export function buildProcessOk() {
    return {
        type: BUILD_PROCESS_OK
    };
}

export const SAVE_PROCESS_FAILED = "SAVE_PROCESS_FAILED";
export function saveProcessFailed() {
    return {
        type: SAVE_PROCESS_FAILED
    };
}


export interface OpenDependencyConstraintModalAction extends IAction {
    warningMessage: string;
}

export const OPEN_DEPENDENCY_CONSTRAINT_SAVE_MODAL = "OPEN_DEPENDENCY_CONSTRAINT_SAVE_MODAL";
export function openDependencyConstrainSaveModal(warningMessage: string): OpenDependencyConstraintModalAction{
    return {
        type: OPEN_DEPENDENCY_CONSTRAINT_SAVE_MODAL,
        warningMessage: warningMessage
    };
}

export interface CloseDependencyConstraintModalAction extends IAction {
    ignoreWarning: boolean;
}

export const CLOSE_DEPENDENCY_CONSTRAINT_SAVE_MODAL = "CLOSE_DEPENDENCY_CONSTRAINT_SAVE_MODAL";
export function closeDependencyConstraintConfirmationModal(payload: { ignoreWarning: boolean}): CloseDependencyConstraintModalAction {
    return {
        type: CLOSE_DEPENDENCY_CONSTRAINT_SAVE_MODAL,
        ignoreWarning: payload.ignoreWarning
    };
}

